//#region IMPORTS
import {
  createInstoreThemeAsync,
  updateInstoreThemeAsync,
} from "../../features/instoreThemes/instoreThemesSlice";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { InstoreTheme } from "../../models/instoreTheme";
import { useEffect } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import SubmitButton from "../genericButtons/submitButton/SubmitButton";
import classNames from "../../helpers/classNames";
//#endregion

//#region INTERFACE
interface Props {
  instoreTheme?: InstoreTheme;
  cancelCUD: () => void;
  disabled?: boolean;
}
//#endregion

export default function InstoreThemeForm({
  instoreTheme,
  cancelCUD,
  disabled = false,
}: Props) {
  //#region SETUP
  // Redux
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector(({ instoreThemes }) => instoreThemes);

  // UseForm
  const {
    reset,
    register,
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
  } = useForm({ mode: "all" });
  //#endregion

  //#region LOGIC
  useEffect(() => {
    if (instoreTheme) reset(instoreTheme);
  }, [instoreTheme, reset]);

  // Submit form
  async function submitForm(data: FieldValues) {
    try {
      if (instoreTheme) {
        await dispatch(
          updateInstoreThemeAsync({
            id: instoreTheme.id,
            values: { name: data.name },
          })
        );
      } else {
        await dispatch(
          createInstoreThemeAsync({
            values: { name: data.name },
          })
        );
      }
    } catch (error) {
      toast.error(`${error}`);
    }
    cancelCUD();
  }
  //#endregion

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <div className="space-y-12">
        <div className="pb-12">
          {/* HEADER */}
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            {instoreTheme
              ? `Update theme: ${instoreTheme.name}`
              : "Create new theme"}
          </h2>

          {/* BODY */}
          <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            {/* name */}
            <fieldset className="sm:col-span-6">
              <label
                htmlFor="name"
                className={
                  errors.name
                    ? "block text-sm font-medium leading-6 text-red-900"
                    : "block text-sm font-medium leading-6 text-gray-900"
                }
              >
                {errors.name ? errors?.name?.message : "theme name"}
              </label>
              <div className="relative mt-2">
                <input
                  placeholder="e.g. My first theme"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                  disabled={disabled || loading}
                  type="text"
                  {...register("name", {
                    required: "Name is required.",
                  })}
                />
              </div>
            </fieldset>
          </div>
        </div>
      </div>

      {/* ACTIONS */}
      <div className="mt-6 flex items-center justify-end gap-x-6">
        <button
          onClick={cancelCUD}
          type="button"
          disabled={disabled || loading}
          className={classNames(
            disabled
              ? "text-gray-300 hover:cursor-not-allowed"
              : "text-rose-700",
            "text-sm font-semibold leading-6"
          )}
        >
          Cancel
        </button>

        <SubmitButton
          conditionals={[isValid]}
          isSubmitting={isSubmitting}
          disabled={disabled || loading}
        />
      </div>
    </form>
  );
}
